import { useCan } from '@refinedev/core'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { ListView, ListViewHeader } from '~/components/refineUi/views/listView'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs'
import {
  DEFAULT_CONFIGURATIONS_RESOURCE,
  MEMBERS_RESOURCE,
} from '~/providers/accessControl'
import { ConfigurationsTab } from './configurationsTab'
import { GeneralTab } from './generalTab'
import { MembersTab } from './membersTab'
import {
  settingsTab,
  settingsTabParams,
  visibleSettingsTabs,
} from './settingsTabs'

/**
 * The host tenant's Settings (`/settings`): one tab per area, the open one
 * in `?tab=` (none for General). Staff and the tenant's owners/admins only
 * (`SETTINGS_RESOURCE` in `accessControl`); Configurations for those who may
 * set the default configurations (`DEFAULT_CONFIGURATIONS_RESOURCE`), Members
 * for those who may manage them (`MEMBERS_RESOURCE`).
 */
export function SettingsPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: membersAccess } = useCan({
    resource: MEMBERS_RESOURCE,
    action: 'list',
  })
  const canSeeMembers = membersAccess?.can === true
  const { data: configurationsAccess } = useCan({
    resource: DEFAULT_CONFIGURATIONS_RESOURCE,
    action: 'list',
  })
  const canSeeConfigurations = configurationsAccess?.can === true
  // a tab the user may not open (yet, while access loads) shows General
  const tabs = visibleSettingsTabs({
    configurations: canSeeConfigurations,
    members: canSeeMembers,
  })
  const tab = settingsTab(searchParams, tabs)

  return (
    <ListView>
      <ListViewHeader
        title={t('settingsPage.title')}
        canCreate={false}
      />
      <Tabs
        value={tab}
        onValueChange={(value) => {
          const next = tabs.find((known) => known === value)
          if (next !== undefined) {
            setSearchParams(settingsTabParams(searchParams, next), {
              replace: true,
            })
          }
        }}
      >
        <TabsList>
          {tabs.map((value) => (
            <TabsTrigger
              key={value}
              value={value}
            >
              {t(`settingsPage.tabs.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value="general"
          className="pt-2"
        >
          <GeneralTab />
        </TabsContent>
        {canSeeConfigurations && (
          <TabsContent
            value="configurations"
            className="pt-2"
          >
            <ConfigurationsTab />
          </TabsContent>
        )}
        {canSeeMembers && (
          <TabsContent
            value="members"
            className="pt-2"
          >
            <MembersTab />
          </TabsContent>
        )}
      </Tabs>
    </ListView>
  )
}
